// @flow

//
// See https://github.com/hongymagic/react-number-input/blob/master/src/react-number-input.js
//
import assign from 'object-assign';
import numeral from 'numeral';
import _ from 'lodash';
import { Component, createElement } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

/**
 * Check if a given value is a valid number.
 *
 * @returns {boolean} True if given value is a valid number.
 * @param value
 */
export function isNumber(value): boolean {
  return typeof value === 'number' && isFinite(value) && !isNaN(value);
}

/**
 * Safe conversion to numeral object. Numeral crashes with the value is
 * object or function.
 *
 * @returns {numeral}
 * @param value
 */
export function toNumeral(value) {
  const type = typeof value;

  if (type === 'object' || type === 'function' || type === 'boolean') {
    return null;
  }

  const n = numeral(value);

  // numeral.js converts empty strings into 0 for no reason, so if given
  // value was not '0' or 0, treat it as null.
  if (n.value() === 0 && (value !== 0 && value !== '0')) {
    return null;
  }

  // numeral.js can sometimes convert values (like '4.5.2') into NaN
  // and we would rather null than NaN.
  if (isNaN(n.value())) {
    return null;
  }

  return n;
}

/**
 * Convert given value to a number type. If conversion fails, returns NaN.
 *
 * @returns {Number} NaN if conversion fails.
 * @param value
 */
export function parseNumber(value) {
  const n = toNumeral(value);
  return n ? n.value() : NaN;
}

/**
 * Apply number formatting to given number value. If given value cannot be
 * converted to a number, returns null.
 *
 * @returns {string|null}
 * @param value
 * @param format
 */
export function formatNumber(value, format = NumberInput.DEFAULT_NUMBER_FORMAT) {
  const n = toNumeral(value);
  return n ? n.format(format) : null;
}

/**
 * <NumberInput /> component
 *
 * @param   {Number} value
 * @returns {Component}
 */
export default class NumberInput extends Component {

  /*
   * See numeral.js formats
   * http://numeraljs.com/
   */
  static DEFAULT_NUMBER_FORMAT = '0,0[.][00]';
  static GEOPT_FORMAT = '0,0[.][00000000]';
  static CURRENCY_FORMAT = '0,0[.]00';
  static INT_FORMAT = '0';

  static propTypes = {
    value: PropTypes.number,
    type: PropTypes.string,
    format: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    autoFocus: PropTypes.bool
  };

  static defaultProps = {
    value: null,
    type: 'tel', // to allow non-numeric characters
    format: NumberInput.DEFAULT_NUMBER_FORMAT,
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
    autoFocus: false
  };

  constructor(props) {
    super(props);

    const value = parseNumber(this.props.value);

    // focused: keep track of the input's focus state
    // numeral: keep track of the input's current value (numeral object)
    this.state = {
      focused: false,
      value: isNumber(value) ? value : ''
    };
  }

  componentDidMount() {
    // focused: check if component is focused after mounting and set state
   this.setState({
     focused: global.document.activeElement === this.refs.input
   })
  }

  componentDidUpdate() {
    if (this.props.autoFocus) {
      ReactDOM.findDOMNode(this.refs.input).focus();
    }
  }

  componentWillReceiveProps(props) {
    // Prevent changing the value via external entry when editing.
    if (!this.state.focused && 'value' in props) {
      const value = parseNumber(props.value)
      this.setState({ value: isNumber(value) ? value : '' })
    }
  }

  onChange = (event: Event) => {
    event.persist();

    // handle comma on price.
    const value = event.target.value.replace(',', '.');

    this.setState(
      { value: value },
      () => {
        if (_.isNull(value)) {
          this.props.onChange(null, event);
        } else {
          const valueNumber = parseFloat(value);
          if (_.isNaN(valueNumber)) {
            this.props.onChange(null, event);
          } else if (_.isNumber(valueNumber)) {
            this.props.onChange(valueNumber, event);
          } else {
          }
        }
      }
    );
  };

  onBlur = (event) => {
    event.persist()
  		let n = toNumeral(event.target.value)

  		// If given value is lower than minimum, set the value to minimum
  		if (n && 'min' in this.props && n.value() < this.props.min) {
  			n = toNumeral(this.props.min)
  		}

  		// If given value is greater than maximum, set the value to maximum
  		if (n && 'max' in this.props && n.value() > this.props.max) {
  			n = toNumeral(this.props.max)
  		}

      // if we have a min and the value is null, we set it to the min.
      if  (_.isNaN(n) || _.isNil(n)) {
        if ('min' in this.props) {
          n = toNumeral(this.props.min);
        }
      }

  		// Set the event target value to corrected value
  		event.target.value = n ? n.value() : ''

  		this.setState(
  			{
  				focused: false,
  				value: n ? n.format(this.props.format) : ''
  			},
  			() => this.props.onBlur(event)
  		)
  };

  onFocus = (event) => {
    event.persist();
    const n = toNumeral(event.target.value);
    this.setState(
      {
        focused: true,
        value: n ? n.value() : ''
      }//,
      //() => this.props.onFocus(event)
    );
  };

  valueAsFormatted() {
    const value = this.state.value;
    const n = toNumeral(value);

    return n ? n.format(this.props.format) : '';
  }

  render() {
    const value = this.state.focused ? this.state.value : this.valueAsFormatted();

    return createElement(
      'input',
      assign(
        {},
        this.props,
        {
          ref: 'input',
          onChange: this.onChange,
          onFocus: this.onFocus,
          onBlur: this.onBlur,
          autoFocus: this.props.autoFocus,
          value,
          className: 'form-control'
        }
      )
    );
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/components/form/NumberInput.js